'use client';

import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { CollectionType } from '@/@types';
import { useUser } from '@/components/context/user';
import { useLocalizedLinks } from '@/components/core/hooks';
import { useRouter } from 'next/navigation';
import { UIComponent } from '../..';
import { Field, Input, Label } from '../../form';
import * as styles from './collection-form.styles';
import { Button, Icon, Link } from '../../atoms';

// eslint-disable-next-line no-unused-vars
const CollectionForm: UIComponent<{ collection?: CollectionType; onSave?: (collection: CollectionType) => any }> = ({ collection, onSave }) => {
  const { api, user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const links = useLocalizedLinks();
  const router = useRouter();

  const [form, setForm] = useState<Omit<CollectionType, 'images'>>({
    id: '',
    name: '',
    isPrivate: false,
  });

  const updateForm = (field: keyof CollectionType, value: any) => {
    setForm({
      ...form,
      [field]: value,
    });
  };

  return (
    <div className={styles.root}>
      <h2 className={styles.title}>{collection ? <Trans>Edit Collection</Trans> : <Trans>Create Collection</Trans>}</h2>
      <Field>
        <Label>
          <Trans>Collection name</Trans>
        </Label>
        <Input
          disabled={isLoading}
          value={form.name}
          onChange={({ target }) => {
            updateForm('name', target.value);
          }}
        />
      </Field>
      <Field>
        <Label>
          <Trans>Type</Trans>
        </Label>
        <Link
          as="span"
          onClick={() => {
            if (isLoading) {
              return;
            }
            updateForm('isPrivate', !form.isPrivate);
          }}
        >
          <Icon name="lock" /> {form.isPrivate ? <Trans>Private</Trans> : <Trans>Public</Trans>}
        </Link>
      </Field>
      <div className={styles.collectionVisibilityDescription}>
        <h4>
          <b>
            <Trans>Public and Private Collections</Trans>
          </b>
        </h4>
        <p>
          <Trans>
            Use <b>public collections</b> to organize everything publicly and keep it readily accessible. Your collections will help both your friends
            and all Peoople users.
          </Trans>
        </p>
        <p>
          <Trans>
            Only you will be able to see your <b>private collections</b>. Use them to save recommendations like movies you want to watch, restaurants
            you want to try, or products you want to buy.
          </Trans>
        </p>
      </div>
      <Button
        variant="primary"
        disabled={isLoading || !form.name}
        onClick={() => {
          setIsLoading(true);
          api
            ?.createCollection({ name: form.name, isPrivate: form.isPrivate! })
            .then((col) => {
              setIsLoading(false);
              return col;
            })
            .then(
              onSave ||
                ((col) => {
                  router.push(
                    links.collection({
                      ...col,
                      owner: user,
                    }),
                  );
                }),
            );
        }}
      >
        {collection ? <Trans>Save collection</Trans> : <Trans>Create collection</Trans>}
      </Button>
    </div>
  );
};

export default CollectionForm;
